// Layout-mode hook — the single source of truth for responsive branching
// across game screens.
//
// Three explicit modes replace the old compact/roomy + narrow flags, which
// made 'compact' do double duty for portrait AND landscape phones (the two
// need opposite arrangements: portrait stacks zones vertically and wants a
// bottom action dock; landscape is starved for height and wants a side rail).
//
//   phonePortrait  — width-starved, height-rich. Rails + bottom dock.
//   phoneLandscape — height-starved. Rails + right-edge action rail.
//   desktop        — roomy enough for the full grid arrangement.
//
// SSR-safe: resolves to phoneLandscape (false/false) without a window,
// corrected on hydration.

import { useEffect, useState } from 'react';

// A viewport is "desktop" when it can host the full arrangement: wide
// enough for a side rail AND tall enough for stacked card rows plus card
// feet. A landscape phone (e.g. 800×340) passes the width test but fails
// the height test, so it correctly stays a phone.
const DESKTOP_QUERY = '(min-width: 768px) and (min-height: 600px)';
const PORTRAIT_QUERY = '(orientation: portrait)';

export type ScreenMode = 'phonePortrait' | 'phoneLandscape' | 'desktop';

export interface LayoutInfo {
  mode: ScreenMode;
  /** Convenience: phonePortrait || phoneLandscape. */
  isPhone: boolean;
  /** Raw orientation signal — also true on desktop portrait monitors. */
  isPortrait: boolean;
}

/** Subscribe to a media query. SSR-safe (returns false without a window). */
export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    setMatch(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatch(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return match;
}

export function useLayoutMode(): LayoutInfo {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const portrait = useMediaQuery(PORTRAIT_QUERY);
  return {
    mode: desktop ? 'desktop' : portrait ? 'phonePortrait' : 'phoneLandscape',
    isPhone: !desktop,
    isPortrait: portrait,
  };
}
